import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { ICourse, IEducation, IPersonCertificate, IPersonCompetence } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import VisibilityCheckbox from '../VisibilityCheckbox'
import HideDetailsSettings from './HideDetailsSettings'
import { findHideSetting, hasHiddenDetails } from '../hideSettings'
import colors from 'constants/colors'
import { useUser } from 'hooks'

interface IEducationsData {
  degrees: IEducation[]
  courses: ICourse[]
  certificates: IPersonCertificate[]
  competences: IPersonCompetence[]
}

interface IEducationProps {
  educations: IEducationsData
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
}

type EducationLikeItem = IEducation | ICourse | IPersonCertificate | IPersonCompetence
type EducationItemType = 'degrees' | 'courses' | 'certificates' | 'competences'

const educationHideSettingTypes: EducationItemType[] = ['degrees', 'courses', 'certificates', 'competences']

const isDegree = (item: EducationLikeItem): item is IEducation => 'school' in item.translations[0]
const isCourseItem = (item: EducationLikeItem): item is ICourse => 'organizer' in item.translations[0]
const isCertificateItem = (item: EducationLikeItem): item is IPersonCertificate => 'certifier' in item.translations[0]
const isCompetenceItem = (item: EducationLikeItem): item is IPersonCompetence => 'issuer' in item.translations[0]

/**
 * Renders the Education component.
 *
 * @param {Object} educations - The educations object.
 * @param {Object} layout - The layout object.
 * @param {Function} hideSection - The function to hide a section.
 * @param {Function} hideItem - The function to hide an item.
 * @param {boolean} expanded - The expanded state of the accordion.
 * @param {Function} setStatus - The function to set the status.
 * @param {Function} handleHideSettings - Saves the hide setting data of a section.
 * @return {React.ReactElement} The rendered Education component.
 * @notExported
 */
const Education: React.FC<IEducationProps> = ({
  educations,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
  handleHideSettings,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { features } = useUser()

  const degrees = educations.degrees
  const courses = educations.courses
  const certificates = educations.certificates
  const competences = educations.competences

  let hidden = false
  let degreeHidden = false
  let courseHidden = false
  let certHidden = false
  let compHidden = false

  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const degreeVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'degrees'
    )
    if (degreeVisibilities.length > 0 && degreeVisibilities.length >= degrees.length) {
      const visibilityMap = new Map(degreeVisibilities.map(item => [item.itemId, item.hidden]))
      degreeHidden = degrees.every(value => visibilityMap.get(value.id) === true)
    }

    const courseVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'courses'
    )
    if (courseVisibilities.length > 0 && courseVisibilities.length >= courses.length) {
      const visibilityMap = new Map(courseVisibilities.map(item => [item.itemId, item.hidden]))
      courseHidden = courses.every(value => visibilityMap.get(value.id) === true)
    }

    const certVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'certificates'
    )
    if (certVisibilities.length > 0 && certVisibilities.length >= certificates.length) {
      const visibilityMap = new Map(certVisibilities.map(item => [item.itemId, item.hidden]))
      certHidden = certificates.every(value => visibilityMap.get(value.id) === true)
    }

    const compVisibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'competences'
    )
    if (compVisibilities.length > 0 && compVisibilities.length >= competences.length) {
      const visibilityMap = new Map(compVisibilities.map(item => [item.itemId, item.hidden]))
      compHidden = competences.every(value => visibilityMap.get(value.id) === true)
    }

    if (degreeHidden && courseHidden && certHidden && compHidden) {
      hidden = true
    }
  }

  const Item: React.FC<{ item: EducationLikeItem }> = ({ item }) => {
    const type: EducationItemType | null = isDegree(item)
      ? 'degrees'
      : isCourseItem(item)
        ? 'courses'
        : isCertificateItem(item)
          ? 'certificates'
          : isCompetenceItem(item)
            ? 'competences'
            : null

    // create date string
    let date = ''
    if (isDegree(item) || isCourseItem(item)) {
      // degree and course
      if (item.startDate) {
        date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
      }
      if (item.endDate) {
        date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
      }
    } else if (isCertificateItem(item) || isCompetenceItem(item)) {
      // certificate
      if (item.issueDate) {
        date += `${convertToDisplayDate(item.issueDate, item.dateResolution)} - `
      }
      if (item.endDate) {
        date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
      }
    }

    let hidden: boolean | IItemVisibility = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter((visibility: IItemVisibility) => visibility.type === type)
      if (visibilities.length > 0) {
        hidden =
          visibilities.find((value: IItemVisibility) => value.itemId === item.id && value.hidden === true) ?? false
      }
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hidden}
              onChange={(checked: boolean) => hideItem(checked, item.id, type ?? '')}
            />
            <Box fontWeight="fontWeightBold" flexGrow={1}>
              {isDegree(item)
                ? chooseDBTranslation(i18n, item).degree
                : isCourseItem(item)
                  ? chooseDBTranslation(i18n, item).course
                  : isCertificateItem(item)
                    ? chooseDBTranslation(i18n, item.Certificate)?.certificate
                    : isCompetenceItem(item)
                      ? chooseDBTranslation(i18n, item.Competence)?.name
                      : null}
            </Box>
            <Box justifyContent="flex-start">{date}</Box>
          </Box>
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box display="inline">
              {isDegree(item)
                ? chooseDBTranslation(i18n, item).school
                : isCourseItem(item)
                  ? chooseDBTranslation(i18n, item).organizer
                  : isCertificateItem(item)
                    ? chooseDBTranslation(i18n, item).certifier
                    : isCompetenceItem(item)
                      ? chooseDBTranslation(i18n, item).issuer
                      : null}
            </Box>
          </Box>
          <Box>
            {isDegree(item)
              ? chooseDBTranslation(i18n, item).description
              : isCourseItem(item)
                ? chooseDBTranslation(i18n, item).description
                : isCertificateItem(item)
                  ? item.url
                  : null}
          </Box>
          {type === 'certificates' && isCertificateItem(item) && (
            <>
              <Box>
                {t('print.education.licence')}
                {': ' + item.licenceNumber}
              </Box>
              <Box>
                {t('print.certificateExpiry')}
                {': ' + `${convertToDisplayDate(item.endDate, item.dateResolution)}`}
              </Box>
            </>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox
          checked={hidden}
          onChange={(checked: boolean) => {
            hideSection(checked, 'education')
          }}
        />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid container alignItems="center">
            <Grid>{t('print.layout.education')}</Grid>
            <Grid sx={{ marginLeft: 1, fontSize: '11pt', color: theme.palette.text.secondary, fontWeight: 'normal' }}>
              {educationHideSettingTypes.some(type => hasHiddenDetails(findHideSetting(layout, type), type))
                ? t('print.hideSetting.enabled')
                : t('print.hideSetting.disabled')}
            </Grid>
          </Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <Grid size={12}>
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <VisibilityCheckbox
              checked={degreeHidden}
              onChange={(checked: boolean) => hideSection(checked, 'degrees')}
            />
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              {t('print.layout.degrees')}
            </Typography>
          </Box>
          <HideDetailsSettings type="degrees" layout={layout} handleHideSettings={handleHideSettings} />
          <Divider />
          {degrees.map((degree: IEducation, index: number) => {
            return (
              <Box key={`degree-${index}`}>
                <Item item={degree} />
                {index + 1 < degrees.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <VisibilityCheckbox
              checked={courseHidden}
              onChange={(checked: boolean) => hideSection(checked, 'courses')}
            />
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              {t('print.layout.courses')}
            </Typography>
          </Box>
          <HideDetailsSettings type="courses" layout={layout} handleHideSettings={handleHideSettings} />
          <Divider />
          {courses.map((course: ICourse, index: number) => {
            return (
              <Box key={`course-${index}`} display="flex" flexDirection="row" flexWrap="wrap" width="100%">
                <Item item={course} />
                {index + 1 < courses.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <VisibilityCheckbox
              checked={certHidden}
              onChange={(checked: boolean) => hideSection(checked, 'certificates')}
            />
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              {t('print.layout.certificates')}
            </Typography>
          </Box>
          <HideDetailsSettings type="certificates" layout={layout} handleHideSettings={handleHideSettings} />
          <Divider />
          {certificates.map((certificate: IPersonCertificate, index: number) => {
            return (
              <Box key={`certificate-${index}`} display="flex" flexDirection="row" flexWrap="wrap" width="100%">
                <Item item={certificate} />
                {index + 1 < certificates.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
          {features.includes('competences') && (
            <>
              <Box display="flex" flexDirection="row" flexWrap="wrap">
                <VisibilityCheckbox
                  checked={compHidden}
                  onChange={(checked: boolean) => hideSection(checked, 'competences')}
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('print.layout.competences')}
                </Typography>
              </Box>
              <HideDetailsSettings type="competences" layout={layout} handleHideSettings={handleHideSettings} />
              <Divider />
              {competences.map((competence: IPersonCompetence, index: number) => {
                return (
                  <Box key={`competence-${index}`} display="flex" flexDirection="row" flexWrap="wrap" width="100%">
                    <Item item={competence} />
                    {index + 1 < competences.length ? <Divider variant="fullWidth" /> : null}
                  </Box>
                )
              })}
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default Education
